import {ChangeDetectionStrategy, Component, computed, Input} from '@angular/core';

import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AksjeeierBeholdningModell, AksjeeierEnkelModell, AksjeklasseModell, LitenIkkeTomStreng, OppdaterBeholdningerSpesifikasjon, OppdaterBeholdningSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import { NgClass, PercentPipe } from "@angular/common";
import {AbstractControl, FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn} from "@angular/forms";
import {MatIconModule} from "@angular/material/icon";
import {ErrorStateMatcher, MatOptionModule} from "@angular/material/core";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {FormaterProsentPipe} from "felles-ng2/pipes/FormaterProsentPipe";
import {Intervall, normaliserIntervaller, normaliserIntervallerStr} from "felles-ng2/typer/aksjenummer";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {VisAlltidErrorStateMatcher} from "felles-ng2/infrastruktur/material/VisAlltidErrorStateMatcher";
import {AksjenummerintervallerPipe} from "felles-ng2/pipes/AksjenummerintervallerPipe";
import {LukkAbonnementer} from "felles-ng2/infrastruktur/rxjs/LukkAbonnementer";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {Aksjeeiersortering, antallAksjerPerAksjeklasse, BeholdningerModellBase, BeholdningModellBase} from "web-ng2/moduler/aksjeeierbok/komponenter/aksjeeier-liste/BeholdningModellBase";
import {MatButtonModule} from "@angular/material/button";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {Temporal} from "temporal-polyfill";
import {erLikeLister} from "felles-ng2/støttefunksjoner/lister";
import {MatSelectModule} from "@angular/material/select";
import {AntallAksjerPerAksjeklasseKomponent} from "web-ng2/moduler/aksjeeierbok/komponenter/antall-aksjer-per-aksjeklasse/antall-aksjer-per-aksjeklasse";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TranslateModule} from "@ngx-translate/core";
import {skrivPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('AksjeeiereRedigerBeholdningKomponent');

@Component({
    selector: 'aksjeeiere-rediger-beholdning',
    templateUrl: './aksjeeiere-rediger-beholdning.html',
    styleUrls: ['./aksjeeiere-rediger-beholdning.scss'],
    imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    FormaterPlainDatePipe,
    FormaterProsentPipe,
    AksjenummerintervallerPipe,
    PercentPipe,
    NgClass,
    InlineMeldingKomponent,
    MatOptionModule,
    MatSelectModule,
    AntallAksjerPerAksjeklasseKomponent,
    TranslateModule
],
    providers: [
        { provide: ErrorStateMatcher, useClass: VisAlltidErrorStateMatcher }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeeiereRedigerBeholdningKomponent {

    @Input() modell!: BeholdningerSkjemamodell;

    erValgtAksjeklasse(a:AksjeklasseModell, b: AksjeklasseModell):boolean{
        return a?.id == b?.id;
    }

    ktrErrorsSomListe(errors:ValidationErrors | null){
        if (erNil(errors)) return [];
        return Object.entries(errors!);
    }
}

export class BeholdningerSkjemamodell extends BeholdningerModellBase<BeholdningSkjemamodell> {

    aksjenummerSkjemakontroller!: FormArray<FormGroup>;
    endredeBeholdninger = computed(() => this.beholdninger.filter(beholdning => beholdning.erEndret()));
    totaltAntallAksjerDifferanse = computed(() => this.totaltAntallAksjer() - this.eksisterendeTotaltAntallAksjer);
    eksisterendeTotaltAntallAksjer = 0;
    eksisterendeTotaltAntallAksjerPerAksjeklasse = new Map<AksjeklasseModell, number>();
    eksisterendeAksjenummerIBruk: Intervall[] = [];

    constructor(aksjeeiere: AksjeeierEnkelModell[], public aksjeklasser:AksjeklasseModell[], public ab: LukkAbonnementer, readonly oversettelser:Oversettelser) {
        super(aksjeeiere, ab, Aksjeeiersortering.etterBeholdning, oversettelser); //sørger for at aksjeeiere er sortert etter beholdning
        this.initSkjema();

        this.eksisterendeTotaltAntallAksjer = this.beholdninger
          .map(beholdning => beholdning.aksjeeier?.naavaerendeBeholdning?.antall ?? 0)
          .reduce((a, b) => a + b, 0);
        this.eksisterendeTotaltAntallAksjerPerAksjeklasse = this.eksisterendeAntallAksjerPerAksjeklasse();
        this.eksisterendeAksjenummerIBruk = normaliserIntervaller(this.beholdninger
          .flatMap(beholdning => normaliserIntervallerStr(beholdning.aksjeeier?.naavaerendeBeholdning?.aksjenummer ?? null)));
    }

    override nyBeholdningModell(aksjeeier: AksjeeierEnkelModell, forelder: BeholdningerSkjemamodell): BeholdningSkjemamodell {
        return new BeholdningSkjemamodell(aksjeeier, forelder);
    }

    inneholderEndringer(){
        return this.beholdninger.some(beholdning => beholdning.erEndret());
    }

    somSpesifikasjon(innfoeringsdato: PlainDate, endringsaarsak: LitenIkkeTomStreng): OppdaterBeholdningerSpesifikasjon {
        return {
            endringsaarsak: endringsaarsak,
            innfoeringsdato: skrivPlainDateSerialisert(innfoeringsdato),
            beholdninger: this.beholdninger.map(beholdning => beholdning.somSpesifikasjon())
        }
    }

    // hjelpemetoder

    /** Registrer skjema og sørg for revalidering av alle aksjeeiere/beholdninger ved endringer  */
    private initSkjema(){
        this.aksjenummerSkjemakontroller = new FormArray(this.beholdninger.map(m =>
          //vi har ikke behov for en typet FormGroup, for typet kunne vi brukt new FormGroup({aksjenummer: m.aksjenummerKtr(l, ...}))
          new FormGroup([m.aksjenummerKtrl, m.aksjeklasseKtrl])));

        //sørger for å validere alle beholdninger ved endringer - nødvendig for at feil indikert på rad B forårsaket av rad A skal reberegnes når rad A endres
        //samt at verdi i felt A skal boble ut via tilknyttet aksjenummerintervaller Signal (som ikke blir oppdatert når feltet ikke er gyldig)
        this.ab.reg(this.aksjenummerSkjemakontroller.statusChanges
          .subscribe(() => {
              this.beholdninger.forEach(b => b.revaliderOgSetAksjenummerintervallerFraFeltDersomGyldig())
          }));
    }

    /** Retunerer en map med ett innslag for hver aksjeklasse i bruker, med antall aksjer som verdi.
     * Beholdninger ikke knyttet til noen aksjeklasse ligger under nøkkelen {@link tomAksjeklasseModell} */
    private eksisterendeAntallAksjerPerAksjeklasse() {
        const beholdninger = this.beholdninger
          .map(beholdning  => ({
              aksjeklasse: beholdning.nåværendeRegBeholdning()?.aksjeklasse ?? null,
              antallAksjer: beholdning.nåværendeRegBeholdning()?.antall ?? 0}));
        return antallAksjerPerAksjeklasse(beholdninger, this.oversettelser);
    }
}

export class BeholdningSkjemamodell extends BeholdningModellBase {
    aksjenummerKtrl!: FormControl<string>;
    aksjeklasseKtrl!: FormControl<AksjeklasseModell | null>;
    erEndret = computed(() => this.beregnErEndret());

    constructor(aksjeeier: AksjeeierEnkelModell, forelder: BeholdningerSkjemamodell) {
        super(aksjeeier, forelder)
        this.initAksjenummerKtrl();
        this.initAksjeklasseKtrl();
    }

    override aksjenummerFeltverdi() {
        return this.aksjenummerKtrl.value;
    }

    somSpesifikasjon(): OppdaterBeholdningSpesifikasjon {
        return {
            aksjeeierId: this.aksjeeier.id,
            aksjenummer: this.aksjenummerFeltverdi(),
            aksjeklasseId: this.aksjeklasseKtrl.value?.id ?? null
        }
    }

    /** For intern bruk */
    revaliderOgSetAksjenummerintervallerFraFeltDersomGyldig() {
        this.aksjenummerKtrl.updateValueAndValidity({onlySelf: false, emitEvent: false});
        this.setAksjenummerintervallerFraFeltDersomGyldig();
    }

    /** For intern bruk */
    nåværendeRegBeholdning():AksjeeierBeholdningModell|null{
        return this.aksjeeier?.naavaerendeBeholdning;
    }

    private initAksjenummerKtrl() {
        this.aksjenummerKtrl = new FormControl('', {nonNullable: true, validators:[this.validerfunksjon()]});
        //eksponer aksjenummerintervaller via Signal, merk at vi kun eksponerer feltverdien når denne er gyldig
        this.forelder.ab.reg(
          this.aksjenummerKtrl.valueChanges.subscribe(verdi => this.setAksjenummerintervallerFraFeltDersomGyldig()));
        //setter initiell verdi etter at valueChanges lytter er reg - ellers får ikke Signalene med seg den initielle tilstanden
        this.aksjenummerKtrl.setValue(this.aksjeeier?.naavaerendeBeholdning?.aksjenummer ?? '');
    }

    private initAksjeklasseKtrl() {
        this.aksjeklasseKtrl = new FormControl<AksjeklasseModell | null>(null);
        //eksponer aksjenummerintervaller via Signal, merk at vi kun eksponerer feltverdien når denne er gyldig
        this.forelder.ab.reg(
          this.aksjeklasseKtrl.valueChanges.subscribe(verdi => this.aksjeklasse.set(verdi)));
        //setter initiell  verdi etter at valueChanges lytter er reg - ellers får ikke Signalene med seg den initielle tilstanden
        this.aksjeklasseKtrl.setValue(this.nåværendeRegBeholdning()?.aksjeklasse ?? null);
    }

    private setAksjenummerintervallerFraFeltDersomGyldig() {
        if (this.aksjenummerKtrl.invalid) return;
        this.setAksjenummerintervaller(this.aksjenummerFeltverdi());
    }

    private beregnErEndret() {
        if (this.nåværendeRegBeholdning()?.aksjeklasse?.id !== this.aksjeklasse()?.id) return true;

        const opprinnelig = normaliserIntervallerStr(this.nåværendeRegBeholdning()?.aksjenummer ?? null);
        const oppdatert = normaliserIntervaller(this.aksjenummerintervaller());
        return !erLikeLister(opprinnelig, oppdatert);
    }

    private validerfunksjon(): ValidatorFn {
        return (aksjenummer: AbstractControl<string>): ValidationErrors | null => this.validerAksjenummer(aksjenummer.value);
    }

}
